/**
 * Centralized icon exports.
 *
 * Import icons from this module instead of lucide-react or local brand SVGs
 * directly. This provides a single inventory of every icon in use and makes
 * the underlying implementation easy to swap later.
 *
 * Usage:
 *   import { Icons } from "@/components/ui/icons";
 *   <Icons.Search className="h-4 w-4" />
 */
import type { SVGProps } from "react";
import { GoogleIcon } from "./google-icon";
export { CliLogo } from "./cli-logo";
import {
  AArrowDown,
  AArrowUp,
  AlertTriangle,
  CircleAlert,
  Archive,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  BarChart2,
  Bell,
  BellOff,
  Bold,
  BookOpen,
  Bookmark,
  Bot,
  Brain,
  Building2,
  Calendar,
  Camera,
  CheckCheck,
  ClipboardPaste,
  CreditCard,
  Crosshair,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CalendarClock,
  Circle,
  Columns3,
  CircleDot,
  Signal,
  Clock,
  Cloud,
  Code2,
  Cog,
  Command,
  Copy,
  CornerLeftUp,
  Download,
  Edit2,
  Edit3,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Film,
  Filter,
  FolderKanban,
  FolderOpen,
  FolderTree,
  Frame,
  GalleryHorizontal,
  Gauge,
  GitBranch,
  GitPullRequest,
  GitCommitHorizontal,
  Github,
  Globe,
  GripVertical,
  Hand,
  Hash,
  Heart,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  HelpCircle,
  Highlighter,
  ImageIcon,
  Inbox,
  Info,
  Italic,
  Kanban,
  Keyboard,
  KeyRound,
  Layers,
  LayoutGrid,
  LifeBuoy,
  Lightbulb,
  Link2,
  Linkedin,
  List,
  ListCollapse,
  ListOrdered,
  ListTodo,
  ListChecks,
  Loader2,
  Lock,
  LogOut,
  Mail,
  Maximize2,
  Menu,
  Mic,
  Minimize2,
  Minus,
  Monitor,
  Moon,
  Package,
  MessageCircle,
  MessageSquare,
  MoreHorizontal,
  MoreVertical,
  MoveDiagonal2,
  MousePointer2,
  MousePointerClick,
  Palette,
  PanelLeft,
  PanelRight,
  PanelTopClose,
  Pause,
  Pilcrow,
  Pin,
  Plane,
  Play,
  Plug,
  Puzzle,
  Underline,
  Unplug,
  Plus,
  Repeat2,
  RefreshCw,
  RotateCcw,
  Network,
  Scan,
  Search,
  Send,
  Settings,
  Shield,
  SlidersHorizontal,
  Smartphone,
  Smile,
  Sofa,
  Sparkles,
  Square,
  StickyNote,
  Strikethrough,
  Sun,
  Table,
  Tag,
  Target,
  Terminal,
  TextQuote,
  Trash2,
  Type,
  Undo2,
  Upload,
  User,
  Users,
  Volume2,
  VolumeX,
  Wand2,
  Wifi,
  WifiOff,
  Workflow,
  Wrench,
  X,
  XCircle,
  Youtube,
  Zap,
  ZoomIn,
  ZoomOut,
  Folder as FolderIcon,
} from "lucide-react";

/** Three rounded bars that remain readable as a small live-mode marker. */
function LiveWaveform(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 16 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M2.5 5v4.5M7.5 2.5v9.5M12.5 4v6.5" />
    </svg>
  );
}

function TikTok(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        fill="currentColor"
        d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07Z"
      />
    </svg>
  );
}

function XLogo(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        fill="currentColor"
        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z"
      />
    </svg>
  );
}

function WhatsApp(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        fill="#25D366"
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"
      />
    </svg>
  );
}

/** The project space: a 2×2 grid of rounded squares in the text color. */
function SpaceIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      fillOpacity={0.7}
      aria-hidden="true"
      {...props}
    >
      <rect x="2" y="2" width="8.5" height="8.5" rx="1.75" />
      <rect x="13.5" y="2" width="8.5" height="8.5" rx="1.75" />
      <rect x="2" y="13.5" width="8.5" height="8.5" rx="1.75" />
      <rect x="13.5" y="13.5" width="8.5" height="8.5" rx="1.75" />
    </svg>
  );
}

export const Icons = {
  Google: GoogleIcon,
  AArrowDown,
  AArrowUp,
  AlertTriangle,
  CircleAlert,
  Archive,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  BarChart2,
  Bell,
  BellOff,
  Bold,
  BookOpen,
  Bookmark,
  Bot,
  Brain,
  Building2,
  Calendar,
  Camera,
  ClipboardPaste,
  CreditCard,
  Crosshair,
  Check,
  CheckCheck,
  CheckCircle: CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CalendarClock,
  Circle,
  Columns3,
  CircleDot,
  Signal,
  Clock,
  Cloud,
  Code: Code2,
  Cog,
  Command,
  Copy,
  CornerLeftUp,
  Download,
  Edit: Edit3,
  Edit2,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Film,
  Filter,
  Folder: FolderIcon,
  FolderKanban,
  FolderOpen,
  FolderTree,
  Frame,
  GalleryHorizontal,
  Gauge,
  GitBranch,
  GitPullRequest,
  GitCommitHorizontal,
  Github,
  Globe,
  GripVertical,
  Hand,
  Hash,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  Heart,
  HelpCircle,
  Highlighter,
  Image: ImageIcon,
  Inbox,
  Info,
  Italic,
  Kanban,
  Keyboard,
  KeyRound,
  Layers,
  LayoutGrid,
  LifeBuoy,
  Lightbulb,
  Link: Link2,
  Linkedin,
  List,
  ListCollapse,
  ListOrdered,
  ListTodo,
  ListChecks,
  Spinner: Loader2,
  LiveWaveform,
  Lock,
  LogOut,
  Mail,
  Maximize: Maximize2,
  Menu,
  Mic,
  Minimize: Minimize2,
  Minus,
  Monitor,
  Moon,
  Package,
  MessageCircle,
  MessageSquare,
  MoreHorizontal,
  MoreVertical,
  MoveDiagonal2,
  MousePointer: MousePointer2,
  MousePointerClick,
  Palette,
  PanelLeft,
  PanelRight,
  PanelTopClose,
  Pause,
  Pilcrow,
  Pin,
  Plane,
  Play,
  Plug,
  Puzzle,
  Underline,
  Unplug,
  Plus,
  RefreshCw,
  Repeat2,
  RotateCcw,
  Scan,
  Search,
  Send,
  Settings,
  Network,
  Shield,
  SlidersHorizontal,
  Smartphone,
  Smile,
  Sofa,
  Space: SpaceIcon,
  Sparkles,
  Square,
  Sun,
  StickyNote,
  Strikethrough,
  Table,
  Tag,
  Target,
  Terminal,
  TextQuote,
  TikTok,
  Trash2,
  Type,
  Undo2,
  Upload,
  User,
  Users,
  Volume2,
  VolumeX,
  Wand2,
  WhatsApp,
  Wifi,
  WifiOff,
  Workflow,
  Wrench,
  X,
  XCircle,
  XLogo,
  Youtube,
  Zap,
  ZoomIn,
  ZoomOut,
} as const;

export type IconName = keyof typeof Icons;
